import { NoteIcon } from '@phosphor-icons/react'
import { times } from 'lodash'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { DeliveryAlert } from '@/components/business/market-shop-restaurant/delivery-alert'
import { NoteAlert } from '@/components/business/market-shop-restaurant/note-alert'
import { PaymentMethodAlert } from '@/components/business/market-shop-restaurant/payment-method-alert'
import { RelativeTime } from '@/components/business/market-shop-restaurant/relative-time'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { popupFactory } from '@/factories'
import { useLongPress } from '@/hooks'
import { MarketShopOrderModel, marketShopOrderStatusDataMap } from '@/models'
import { maxOrderItemsShownInCard } from '@/pages/market-shop-restaurant/market-shop-restaurant.constants'
import { useMarketShopRestaurantStore } from '@/pages/market-shop-restaurant/market-shop-restaurant.store'
import { OrderDetailsModal } from '@/pages/market-shop-restaurant/order-details-modal'

import * as S from './order-card.styled'
import { OrderStatusControls } from './order-status-controls'

export type OrderCardProps = {
  order: MarketShopOrderModel
  previewColumnStatus?: MarketShopOrderModel['status']
  isOverlay?: boolean
  isPreview?: boolean
}

export const OrderCard = ({ order, isOverlay, previewColumnStatus, isPreview }: OrderCardProps) => {
  const { t } = useTranslation(['market-shop-restaurant'])
  const { id, createdAt, status, items, orderNotes } = order

  const {
    newOrdersSet,
    updatingOrders,
    draggedOrder,
    setDraggedOrder,
    dropTargetColumn,
    setDropTargetColumn,
    changeOrderStatus,
    removeOrderFromNewSet,
    updateOrderStatus,
  } = useMarketShopRestaurantStore()

  const isNewOrder = newOrdersSet.has(order.id) && status === 'new'
  const isUpdating = updatingOrders.has(order.id)
  const isBeingDragged = draggedOrder?.id === id && !isUpdating
  const moreThanLimitCount = items.length - maxOrderItemsShownInCard

  const longPressProps = useLongPress({
    onClick: () => {
      removeOrderFromNewSet(order)
      popupFactory.open(OrderDetailsModal, { order })
    },
    onLongPress: (position) => {
      // NOTE: if Order is completed - the card shouldn't be draggable
      if (status === 'completed' || isUpdating) return
      setDraggedOrder(order, position)
    },
  })

  useEffect(() => {
    // NOTE: If the order's status is changed within `OrderDetailsModal`, we need to update/sync its' status for `popupFactory`,
    // so it shows the correct order within modal as well
    const modalProps = popupFactory.checkIfOpen(OrderDetailsModal)
    if (!modalProps || modalProps.order.id !== order.id) return
    popupFactory.update(OrderDetailsModal, { order })
  }, [])

  useEffect(() => {
    if (!isBeingDragged) return

    const handleRelease = () => {
      if (
        dropTargetColumn &&
        dropTargetColumn !== status &&
        marketShopOrderStatusDataMap[status].validTransitions.includes(dropTargetColumn)
      ) {
        void updateOrderStatus(order, dropTargetColumn)
      }
      setDraggedOrder(null, { x: 0, y: 0 })
      setDropTargetColumn(null)
    }

    window.addEventListener('touchend', handleRelease)
    window.addEventListener('mouseup', handleRelease)

    return () => {
      window.removeEventListener('touchend', handleRelease)
      window.removeEventListener('mouseup', handleRelease)
    }
  }, [
    isBeingDragged,
    dropTargetColumn,
    order,
    status,
    changeOrderStatus,
    setDraggedOrder,
    setDropTargetColumn,
    updateOrderStatus,
  ])

  return (
    <S.OrderCard
      $status={previewColumnStatus || status}
      $isBeingDragged={isBeingDragged}
      $isOverlay={isOverlay}
      $isNewOrder={isNewOrder}
      $isPreview={isPreview}
      data-order-number={id}
      {...longPressProps}
    >
      <S.Header>
        <Typography
          as="span"
          size="text-large"
          weight="semibold"
          color="black800"
          breakLines={true}
        >
          #{id}
        </Typography>
        <RelativeTime createdAt={createdAt} />
      </S.Header>
      <S.Content>
        <S.OrderedItems>
          {times(Math.min(maxOrderItemsShownInCard, items.length)).map((index) => {
            const { id, productId, quantity, note } = items[index]

            return (
              <li key={id}>
                <Typography as="span" size="text-medium" weight="semibold" color="black800">
                  {t('market-shop-restaurant:orderItem.quantity', { quantity })}
                </Typography>
                &nbsp;
                <Typography
                  as="span"
                  size="text-medium"
                  weight="medium"
                  color="black500"
                  breakLines={true}
                >
                  {productId.summary}
                </Typography>
                {note && (
                  <>
                    &nbsp;
                    <Icon icon={NoteIcon} color="orange500" size="2rem" />
                  </>
                )}
              </li>
            )
          })}
          {moreThanLimitCount > 0 && (
            <li>
              <Typography as="span" size="text-medium" weight="semibold" color="orange500">
                +{moreThanLimitCount}
              </Typography>
              &nbsp;
              <Typography
                as="span"
                size="text-medium"
                weight="medium"
                color="orange500"
                breakLines={true}
              >
                {t('market-shop-restaurant:orderCard.moreItems', { count: moreThanLimitCount })}
              </Typography>
            </li>
          )}
        </S.OrderedItems>
        <S.Infos>
          {orderNotes && <NoteAlert text={orderNotes} />}
          <PaymentMethodAlert order={order} />
          <DeliveryAlert order={order} />
        </S.Infos>
        <OrderStatusControls order={order} disabled={isBeingDragged} />
      </S.Content>
    </S.OrderCard>
  )
}
